import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { ListChecks, Plus, Loader2, Trash2, Play, Check, Link2, Paperclip, Send, Users, Clock, ImagePlus, X, RotateCcw, Timer } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { getTasks, getTask, createTask, updateTask, deleteTask, addTaskComment, getEmployees } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { TasksTabs } from "../components/tasks/TasksTabs";

const ST = {
  todo: { label: "À faire", cls: "bg-slate-100 text-slate-600" },
  in_progress: { label: "En cours", cls: "bg-amber-100 text-amber-700" },
  done: { label: "Terminée", cls: "bg-emerald-100 text-emerald-700" },
};

const fmtDur = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  if (h) return `${h}h ${String(m).padStart(2, "0")}min`;
  if (m) return `${m}min ${String(r).padStart(2, "0")}s`;
  return `${r}s`;
};

const LiveTimer = ({ task }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(iv);
  }, []);
  const elapsed = (task.time_spent_seconds || 0) + (Date.now() - new Date(task.started_at).getTime()) / 1000;
  return (
    <span className="flex items-center gap-1 text-[11px] font-bold font-mono text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full animate-pulse" data-testid={`task-timer-${task.id}`}>
      <Timer size={11} /> {fmtDur(elapsed)}
    </span>
  );
};

const TaskCard = ({ t, employees, isManager, myEmpId, onChanged, onDelete }) => {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const assigned = t.assign_all || (myEmpId && (t.employee_ids || []).includes(myEmpId));
  const names = t.assign_all ? "Tous les employés" : employees.filter((e) => (t.employee_ids || []).includes(e.id)).map((e) => e.first_name).join(", ");

  const setStatus = async (status) => {
    try { setBusy(true); await updateTask(t.id, { status }); toast.success(ST[status].label); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const sendComment = async () => {
    if (!comment.trim()) return;
    try { setBusy(true); await addTaskComment(t.id, comment); setComment(""); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const openAttachment = async (idx, name) => {
    try {
      const r = await getTask(t.id);
      const a = (r.data.attachments || [])[idx];
      if (!a?.data) { toast.error("Fichier introuvable"); return; }
      const link = document.createElement("a");
      link.href = a.data;
      link.download = name || a.name || "fichier";
      link.click();
    } catch { toast.error("Erreur de téléchargement"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`task-card-${t.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-900">{t.title}</p>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ST[t.status]?.cls}`} data-testid={`task-status-${t.id}`}>{ST[t.status]?.label}</span>
            {t.status === "in_progress" && t.started_at && <LiveTimer task={t} />}
            {t.status === "done" && (t.time_spent_seconds || 0) > 0 && (
              <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`task-total-time-${t.id}`}>
                <Timer size={10} /> Temps total : {fmtDur(t.time_spent_seconds)}
              </span>
            )}
            {t.due_date && <span className="flex items-center gap-1 text-[10px] text-slate-400"><Clock size={10} /> {t.due_date}</span>}
          </div>
          <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1"><Users size={10} /> {names || "—"} · par {t.created_by}</p>
          {t.description && <p className="text-xs text-slate-600 mt-1.5 whitespace-pre-wrap">{t.description}</p>}
          {(t.links || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1.5">
              {t.links.map((l, i) => <a key={i} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] text-[#26788A] hover:underline"><Link2 size={11} /> {l.label || l.url}</a>)}
            </div>
          )}
          {(t.attachments || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1.5">
              {t.attachments.map((a, i) => (
                <button key={i} onClick={() => openAttachment(i, a.name)} className="flex items-center gap-1 text-[11px] text-slate-500 bg-slate-50 hover:bg-slate-100 px-2 py-0.5 rounded-md" data-testid={`task-attachment-${t.id}-${i}`}>
                  <Paperclip size={11} /> {a.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {assigned && t.status === "todo" && (
            <Button size="sm" className="h-8 bg-amber-500 hover:bg-amber-600 text-white" disabled={busy} onClick={() => setStatus("in_progress")} data-testid={`start-task-${t.id}`}>
              <Play size={13} className="mr-1" /> Commencer
            </Button>
          )}
          {assigned && t.status === "in_progress" && (
            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={() => setStatus("done")} data-testid={`finish-task-${t.id}`}>
              <Check size={13} className="mr-1" /> Terminer
            </Button>
          )}
          {(assigned || isManager) && t.status === "done" && (
            <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => setStatus("in_progress")} data-testid={`resume-task-${t.id}`}>
              <RotateCcw size={13} className="mr-1" /> Reprendre
            </Button>
          )}
          {isManager && (
            <button className="text-slate-300 hover:text-red-500 p-1.5" onClick={() => onDelete(t)} data-testid={`delete-task-${t.id}`}><Trash2 size={15} /></button>
          )}
        </div>
      </div>
      {/* Conversation */}
      <div className="mt-3 pt-3 border-t border-slate-100">
        {(t.comments || []).map((c) => (
          <div key={c.id} className="flex gap-2 text-xs mb-1.5">
            <span className="font-semibold text-[#26788A] shrink-0">{c.author} :</span>
            <span className="text-slate-600">{c.text}</span>
          </div>
        ))}
        <div className="flex gap-2 mt-1">
          <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Écrire un message…" className="h-8 text-xs"
            onKeyDown={(e) => e.key === "Enter" && sendComment()} data-testid={`comment-input-${t.id}`} />
          <Button size="sm" variant="outline" className="h-8" disabled={busy || !comment.trim()} onClick={sendComment} data-testid={`send-comment-${t.id}`}><Send size={13} /></Button>
        </div>
      </div>
    </div>
  );
};

export default function TasksPage() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || !!user?.can_manage_events;
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const addFiles = (files) => {
    Array.from(files).forEach((f) => {
      if (f.size > 3.7 * 1024 * 1024) { toast.error(`${f.name} dépasse 3,5 Mo`); return; }
      const reader = new FileReader();
      reader.onload = () => setForm((prev) => prev ? { ...prev, attachments: [...(prev.attachments || []), { name: f.name, data: reader.result }] } : prev);
      reader.readAsDataURL(f);
    });
  };

  const load = useCallback(() => {
    getTasks().then((r) => setTasks(r.data)).catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); getEmployees().then((r) => setEmployees(r.data)).catch(() => {}); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    if (!form.due_date) { toast.error("Une date d'échéance est requise (la tâche apparaît au planning)"); return; }
    try {
      setSaving(true);
      await createTask({ ...form, links: form.link_url ? [{ label: form.link_label || form.link_url, url: form.link_url }] : [] });
      toast.success("Tâche attribuée — les employés ont été notifiés");
      setForm(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Supprimer la tâche « ${t.title} » ?`)) return;
    try { await deleteTask(t.id); toast.success("Tâche supprimée"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-4xl w-full min-w-0 mx-auto pb-24" data-testid="tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ListChecks size={24} className="text-[#26788A]" /> Tâches</h1>
        {isManager && (
          <Button size="sm" className="bg-gradient-to-r from-[#26788A] to-[#1D5E6C] text-white" onClick={() => setForm({ title: "", description: "", assign_all: false, employee_ids: [], due_date: "", link_url: "", link_label: "", attachments: [] })} data-testid="add-task-btn">
            <Plus size={14} className="mr-1" /> Nouvelle tâche
          </Button>
        )}
      </div>

      <TasksTabs />
      {loading ? <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-slate-300" /></div>
        : tasks.length === 0 ? (
          <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl"><p className="text-slate-500 text-sm">Aucune tâche pour l'instant.</p></div>
        ) : (
          <div className="space-y-3">
            {tasks.map((t) => <TaskCard key={t.id} t={t} employees={employees} isManager={isManager} myEmpId={user?.employee_id} onChanged={load} onDelete={handleDelete} />)}
          </div>
        )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg" data-testid="task-form-dialog">
          <DialogHeader><DialogTitle>Nouvelle tâche</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label className="mb-1 block text-xs">Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
              <div><Label className="mb-1 block text-xs">Description / notes</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1 block text-xs">Échéance</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                <div><Label className="mb-1 block text-xs">Lien (optionnel)</Label><Input placeholder="https://…" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} /></div>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Médias / fichiers (photos, PDF… max 3,5 Mo)</Label>
                <input ref={fileRef} type="file" multiple accept="image/*,application/pdf,video/*,audio/*" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="task-file-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="add-media-btn">
                  <ImagePlus size={13} className="mr-1" /> Ajouter des médias
                </Button>
                {(form.attachments || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.attachments.map((a, i) => (
                      <span key={i} className="flex items-center gap-1 text-[11px] bg-slate-100 text-slate-600 px-2 py-1 rounded-full" data-testid={`form-attachment-${i}`}>
                        {a.data?.startsWith("data:image") && <img src={a.data} alt="" className="w-5 h-5 rounded object-cover" />}
                        <Paperclip size={10} /> {a.name}
                        <button onClick={() => setForm({ ...form, attachments: form.attachments.filter((_, x) => x !== i) })} className="text-slate-400 hover:text-red-500" data-testid={`remove-attachment-${i}`}><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-700 mb-2">
                  <Checkbox checked={form.assign_all} onCheckedChange={(v) => setForm({ ...form, assign_all: !!v })} data-testid="assign-all-checkbox" /> Attribuer à tous les employés
                </label>
                {!form.assign_all && (
                  <div className="flex flex-wrap gap-1.5">
                    {employees.map((e) => (
                      <button key={e.id}
                        className={`text-xs px-2.5 py-1 rounded-full border ${form.employee_ids.includes(e.id) ? "bg-[#26788A] text-white border-[#26788A]" : "bg-white text-slate-600 border-slate-200"}`}
                        onClick={() => setForm({ ...form, employee_ids: form.employee_ids.includes(e.id) ? form.employee_ids.filter((x) => x !== e.id) : [...form.employee_ids, e.id] })}
                        data-testid={`task-emp-${e.id}`}>
                        {e.first_name} {e.last_name?.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setForm(null)}>Annuler</Button>
                <Button className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={save} disabled={saving} data-testid="save-task-btn">
                  {saving && <Loader2 size={13} className="animate-spin mr-1" />} Attribuer la tâche
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
